import { Component, computed, inject, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatDivider } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { MonitoringService } from '../../../../generated-sources/openapi/api/monitoring.service';
import { LoginService } from '../../core/services/login.service';
import { SearchStateService } from '../../core/services/search-state.service';
import { SignalrService } from '../../core/services/signalr.service';
import { Condominium } from '../../../../generated-sources/openapi/model/condominium';
import { House } from '../../../../generated-sources/openapi/model/house';
import { InvitationDetail } from '../../../../generated-sources/openapi/model/invitationDetail';

@Component({
  selector: 'app-principal-window-search',
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDivider,
    MatProgressSpinnerModule,
    MatTableModule,
    MatIconModule,
    MatTooltipModule,
    DatePipe
  ],
  templateUrl: './principal-window-search.html',
  styleUrl: './principal-window-search.css',
})
export class PrincipalWindowSearch implements OnInit {

  private _bottomSheetRef = inject<MatBottomSheetRef<PrincipalWindowSearch>>(MatBottomSheetRef);
  private token!: string;

  readonly STATUS_VENCIDO = 'VENCIDO';

  lstInvitations!: typeof this.searchState.lstInvitations;
  readonly qrTableColumns = ['nameCondominium', 'name', 'nameResident', 'house', 'date1', 'date2', 'status', 'actions'];

  condominiums!: typeof this.searchState.condominiums;
  houses!: typeof this.searchState.houses;
  loading = false;

  private fb = inject(FormBuilder);

  form = this.fb.group({
    idCondominium: this.fb.control<string | number | null>(null, Validators.required),
    idHouse: this.fb.control<string | number | null>(null, Validators.required),
  });

  formInvitation = this.fb.group({
    qrString: this.fb.control<string | null>(null, Validators.required),
  });


  private condominiumSearch = toSignal(
    this.form.controls.idCondominium.valueChanges,
    { initialValue: null }
  );

  private houseSearch = toSignal(
    this.form.controls.idHouse.valueChanges,
    { initialValue: null }
  );

  filteredCondominiums = computed(() => {
    const search = this.condominiumSearch();
    if (typeof search !== 'string') return this.condominiums();
    return this.condominiums().filter(c =>
      c.name?.toLowerCase().includes(search.toLowerCase())
    );
  });

  filteredHouses = computed(() => {
    const search = this.houseSearch();
    if (typeof search !== 'string') return this.houses();
    return this.houses().filter(h =>
      h.street?.toLowerCase().includes(search.toLowerCase())
    );
  });

  displayCondominium = (id: number | null): string =>
    this.condominiums().find(c => c.id === id)?.name ?? '';

  displayHouse = (id: number | null): string =>
    this.houses().find(h => h.id === id)?.street ?? '';



  constructor(
    private monitoringService: MonitoringService,
    private authService: LoginService,
    private searchState: SearchStateService,
    private router: Router,
    readonly signalrService: SignalrService
  ) {
    this.condominiums = this.searchState.condominiums;
    this.houses = this.searchState.houses;
    this.lstInvitations = this.searchState.lstInvitations;
  }

  ngOnInit(): void {
    this.token = this.authService.getToken() ?? '';
    this.getCondominiums();
  }

  getCondominiums(): void {
    // Escenario 1: la lista ya fue cargada anteriormente (caché en el servicio).
    // Se omite la llamada HTTP y se restaura directamente el estado del formulario.
    if (this.searchState.condominiums().length > 0) {
      const savedCondominium = this.searchState.idCondominium();
      if (savedCondominium) {
        this.form.controls.idCondominium.setValue(savedCondominium, { emitEvent: false });
        this.getHouseAndRestoreState();
      }
      return;
    }

    // Escenario 2: primera carga, se hace la llamada HTTP y se guarda el resultado
    // en el servicio. Una vez cargado, se restaura el estado del formulario si existe.
    this.monitoringService.monitoringCondominiumGet(this.token).subscribe({
      next: (response) => {
        this.searchState.setCondominiums(response.data ?? []);
        const savedCondominium = this.searchState.idCondominium();
        if (savedCondominium) {
          this.form.controls.idCondominium.setValue(savedCondominium, { emitEvent: false });
          this.getHouseAndRestoreState();
        }
      },
      error: (error) => {
        console.error('Error fetching condominiums:', error);
      }
    });
  }

  getHouse(idCondominium: number): void {
    this.loading = true;
    this.monitoringService.monitoringHouseIdCondominiumGet(this.token, idCondominium).subscribe({
      next: (response) => {
        this.searchState.setHouses(response.data ?? []);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error fetching houses:', error);
        this.loading = false;
      }
    });
  }

  private getHouseAndRestoreState(): void {
    // Las casas ya están en el servicio (cargadas en la sesión anterior),
    // solo se restaura el valor del formulario sin hacer llamada HTTP.
    const savedHouse = this.searchState.idHouse();
    if (savedHouse) {
      this.form.controls.idHouse.setValue(savedHouse, { emitEvent: false });
    }
  }

  onCondominiumSelected(event: MatAutocompleteSelectedEvent): void {
    const id = event.option.value as number;
    this.searchState.setCondominium(id ?? null);
    this.form.controls.idHouse.setValue(null, { emitEvent: false });
    if (id) {
      this.getHouse(id);
    }
  }

  onHouseSelected(event: MatAutocompleteSelectedEvent): void {
    const id = event.option.value as number;
    this.searchState.setHouse(id ?? null);
  }

  search(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this._bottomSheetRef.dismiss(this.form.value);
  }

  searchByQr(): void {
    if (this.formInvitation.invalid) {
      this.formInvitation.markAllAsTouched();
      return;
    }
    this._bottomSheetRef.dismiss(this.formInvitation.value);
  }

  goToRegister(invitation: InvitationDetail): void {
    const state = {
      invitation,
      idCondominium: this.searchState.idCondominium() ?? null,
    };
    this._bottomSheetRef.dismiss();

    // nos salimos a otra ruta para que si estamos en la ruta ya vuelva a entrar al registro,
    // ya que sino el registro detecta que ya estas ahi y ya no carga nuevamente
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
      this.router.navigate(['/register/new'], { state })
    );
  }
}
